/* ---- Copied from https://github.com/toss/es-toolkit ---- */

// MIT License

// Copyright (c) 2024 Viva Republica, Inc

// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:

// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.

// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
// SOFTWARE.

// ---

// Some of the test code in this software comes from the lodash library (https://github.com/lodash/lodash).

// The copyright for this imported test code from lodash is owned by the JS Foundation and other contributors <https://js.foundation/>.

/**
 * ---- INTERNAL ONLY - SUBJECT TO BREAKING CHANGES ----
 * Checks if a given value is a plain object.
 *
 * @param value - The value to check.
 * @returns True if the value is a plain object, otherwise false.
 * @see [source](https://github.com/toss/es-toolkit/blob/6621df110ab061e644b855fd63236267881ba2a3/src/predicate/isPlainObject.ts)
 *
 * @example
 * ```typescript
 * // ✅👇 True
 *
 * isPlainObject({ });                       // ✅
 * isPlainObject({ key: 'value' });          // ✅
 * isPlainObject({ key: new Date() });       // ✅
 * isPlainObject(new Object());              // ✅
 * isPlainObject(Object.create(null));       // ✅
 * isPlainObject({ nested: { key: true} });  // ✅
 * isPlainObject(new Proxy({}, {}));         // ✅
 * isPlainObject({ [Symbol('tag')]: 'A' });  // ✅
 *
 * // ✅👇 (cross-realms, node context, workers, ...)
 * const runInNewContext = await import('node:vm').then(
 *     (mod) => mod.runInNewContext
 * );
 * isPlainObject(runInNewContext('({})'));   // ✅
 *
 * // ❌👇 False
 *
 * class Test { };
 * isPlainObject(new Test())           // ❌
 * isPlainObject(10);                  // ❌
 * isPlainObject(null);                // ❌
 * isPlainObject('hello');             // ❌
 * isPlainObject([]);                  // ❌
 * isPlainObject(new Date());          // ❌
 * isPlainObject(new Uint8Array([1])); // ❌
 * isPlainObject(Buffer.from('ABC'));  // ❌
 * isPlainObject(Promise.resolve({})); // ❌
 * isPlainObject(Object.create({}));   // ❌
 * isPlainObject(new (class Cls {}));  // ❌
 * isPlainObject(globalThis);          // ❌,
 * ```
 */
export function isPlainObject(value) {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const proto = Object.getPrototypeOf(value);
  const hasObjectPrototype = proto === null || proto === Object.prototype ||
  // Required to support node:vm.runInNewContext({})
  Object.getPrototypeOf(proto) === null;
  if (!hasObjectPrototype) {
    return false;
  }
  return Object.prototype.toString.call(value) === '[object Object]';
}
function merge(target, source) {
  const sourceKeys = Object.keys(source);
  for (let i = 0; i < sourceKeys.length; i++) {
    const key = sourceKeys[i];
    const sourceValue = source[key];
    const targetValue = target[key];
    if (Array.isArray(sourceValue)) {
      if (Array.isArray(targetValue)) {
        // @ts-ignore - Type 'string' cannot be used to index type 'T'
        target[key] = merge(targetValue, sourceValue);
      } else {
        // @ts-ignore - Type 'string' cannot be used to index type 'T'
        target[key] = merge([], sourceValue);
      }
    } else if (isPlainObject(sourceValue)) {
      if (isPlainObject(targetValue)) {
        // @ts-ignore - Type 'string' cannot be used to index type 'T'
        target[key] = merge(targetValue, sourceValue);
      } else {
        // @ts-ignore - Type 'string' cannot be used to index type 'T'
        target[key] = merge({}, sourceValue);
      }
    } else if (targetValue === undefined || sourceValue !== undefined) {
      // @ts-ignore - Type 'string' cannot be used to index type 'T'
      target[key] = sourceValue;
    }
  }
  return target;
}
/**
 * Deeply merges multiple objects into a new object.
 *
 * This function performs a deep merge, meaning nested objects and arrays are merged recursively.
 * If a property exists in multiple source objects, later objects take precedence.
 * If a property in a source object is undefined, it will not overwrite a defined property from earlier objects.
 * Arrays are merged by index, with later arrays overwriting elements at the same index.
 *
 * This function creates a new object and does not mutate any input objects.
 *
 * @param overrides - The source objects to merge. Objects appearing later in the arguments take precedence. Undefined values are ignored.
 * @returns A new object containing the deep-merged properties from all source objects.
 *
 * @template T - Tuple type of the source objects.
 *
 * @see [source](https://github.com/toss/es-toolkit/blob/feb5c35606408cba63a1527005426475a0e68fe8/src/object/merge.ts)
 *
 * @example
 * // Basic usage
 * const obj1 = { a: 1, b: { x: 1, y: 2 } };
 * const obj2 = { b: { y: 3, z: 4 }, c: 5 };
 * const result = deepMerge(obj1, obj2);
 * console.log(result);
 * // Output: { a: 1, b: { x: 1, y: 3, z: 4 }, c: 5 }
 *
 * @example
 * // Array merging
 * const obj1 = { a: [1, 2], b: { x: 1 } };
 * const obj2 = { a: [3], b: { y: 2 } };
 * const result = deepMerge(obj1, obj2);
 * console.log(result);
 * // Output: { a: [3, 2], b: { x: 1, y: 2 } }
 *
 * @example
 * // Multiple objects
 * const obj1 = { a: 1 };
 * const obj2 = { b: 2 };
 * const obj3 = { c: 3 };
 * const result = deepMerge(obj1, obj2, obj3);
 * console.log(result);
 * // Output: { a: 1, b: 2, c: 3 }
 *
 * @example
 * // With undefined values
 * const obj1 = { a: 1, b: 2 };
 * const obj2 = undefined;
 * const obj3 = { c: 3 };
 * const result = deepMerge(obj1, obj2, obj3);
 * console.log(result);
 * // Output: { a: 1, b: 2, c: 3 }
 *
 * @example
 * // No arguments returns empty object
 * const result = deepMerge();
 * console.log(result);
 * // Output: {}
 */
export function deepMerge(...overrides) {
  return overrides.reduce((acc, override) => {
    if (override !== undefined) {
      return merge(acc, override);
    }
    return acc;
  }, {});
}